/**
 * Per-metric "readiness clock" projections for A/B experiments.
 *
 * Different outcome families converge on very different timelines: a continuous
 * metric (time-on-page) reaches significance with far fewer visitors than a
 * low-baseline binary conversion. The running view therefore needs one clock
 * per metric, not a single shared progress bar.
 *
 * Each clock answers: "is this metric significant yet, and if not, roughly how
 * many more days until it is?" The projection generalises the CI half-width
 * shrinkage estimate already used for the conversion diff on the staff list:
 *
 *   - Binary metric (conversion, bounce): the CI half-width on the difference
 *     shrinks ~1/sqrt(N). To pull the half-width down to |effect| (the edge of
 *     significance) N must grow by (halfWidth / effect)^2, and — assuming
 *     visitor accumulation is linear in time — days grow by the same factor.
 *   - Continuous metric (time-on-page): the Mann-Whitney z grows ~sqrt(N). To
 *     reach |z| = z_95 N must grow by (z_95 / |z|)^2. Same shrinkage shape.
 *
 * The estimate assumes the observed effect is the true effect, which is exactly
 * what makes early experiments noisy. Treat the day count as a ballpark, not a
 * deadline — the same caveat the existing conversion projection carries.
 */

/** Two-sided z for 95% confidence — the significance boundary for every clock. */
const Z_95 = 1.96;

/** Projections above this many days are reported as `insufficient` (effectively flat). */
const MAX_PROJECTED_DAYS = 365;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * A "bounce after Rose seen" comparison is harder to read when the two arms see
 * Rose at markedly different points in the page-load bootstrap window (typically
 * ~0.5-2s). Above this per-variant gap in time-to-first-Rose, both experiment
 * views flag the bounce comparison. Shared so the client clocks and the staff
 * detail page stay in sync.
 */
export const LARGE_TIME_TO_ROSE_DELTA_MS = 500;

export type MetricClockKey = 'conversion' | 'time_on_page' | 'bounce' | 'pricing';

export type MetricClockState =
    | 'significant_positive'
    | 'significant_negative'
    | 'collecting'
    | 'insufficient';

export interface MetricClock {
    key: MetricClockKey;
    state: MetricClockState;
    /** Rough days-to-significance projection. Set only when `state === 'collecting'`. */
    daysRemaining: number | null;
}

/** A binary-rate metric whose significance is read off the diff CI. */
export interface BinaryMetricInput {
    diff: number | null;
    ciLow: number | null;
    ciHigh: number | null;
}

/** A continuous metric whose significance is read off the Mann-Whitney z. */
export interface DurationMetricInput {
    z: number | null;
}

/**
 * One metric's clock input. Binary metrics carry the diff CI; continuous
 * metrics carry the Mann-Whitney z. The caller (the client view, mapping over
 * the shared metric descriptors) supplies the list and its order, so adding a
 * metric never touches this file.
 */
export interface MetricClockMetricInput {
    key: MetricClockKey;
    kind: 'binary' | 'continuous';
    diff?: number | null;
    ciLow?: number | null;
    ciHigh?: number | null;
    z?: number | null;
}

export interface MetricClockInput {
    /** ISO start date of the experiment, used to derive elapsed days. */
    startDate: string | null;
    /** Override "now" for deterministic tests. Defaults to `Date.now()`. */
    nowMs?: number;
    /** Metrics to project, in display order. */
    metrics: MetricClockMetricInput[];
}

/** Elapsed days since `startDate`, or null when unusable. */
function elapsedDays(startDate: string | null, nowMs: number): number | null {
    if (!startDate) return null;
    const startedMs = Date.parse(startDate);
    if (!Number.isFinite(startedMs)) return null;
    const elapsedMs = nowMs - startedMs;
    if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return null;
    return elapsedMs / MS_PER_DAY;
}

/**
 * Project days remaining given the multiplicative factor by which total N must
 * grow. Returns null (→ caller maps to `insufficient`) when the projection is
 * unusable or absurdly large.
 */
function projectDaysRemaining(daysElapsed: number | null, growthFactor: number): number | null {
    if (daysElapsed == null) return null;
    if (!Number.isFinite(growthFactor) || growthFactor <= 1) return null;
    const remaining = Math.max(1, Math.ceil(daysElapsed * (growthFactor - 1)));
    if (remaining > MAX_PROJECTED_DAYS) return null;
    return remaining;
}

function binaryClock(
    key: MetricClockKey,
    input: BinaryMetricInput,
    daysElapsed: number | null
): MetricClock {
    const { diff, ciLow, ciHigh } = input;
    if (ciLow == null || ciHigh == null) {
        return { key, state: 'insufficient', daysRemaining: null };
    }
    if (ciLow > 0) return { key, state: 'significant_positive', daysRemaining: null };
    if (ciHigh < 0) return { key, state: 'significant_negative', daysRemaining: null };

    const effect = diff == null ? 0 : Math.abs(diff);
    const halfWidth = (ciHigh - ciLow) / 2;
    if (effect <= 0 || halfWidth <= 0) {
        return { key, state: 'insufficient', daysRemaining: null };
    }
    const daysRemaining = projectDaysRemaining(daysElapsed, (halfWidth / effect) ** 2);
    return daysRemaining == null
        ? { key, state: 'insufficient', daysRemaining: null }
        : { key, state: 'collecting', daysRemaining };
}

function durationClock(
    key: MetricClockKey,
    input: DurationMetricInput,
    daysElapsed: number | null
): MetricClock {
    const { z } = input;
    if (z == null || !Number.isFinite(z)) {
        return { key, state: 'insufficient', daysRemaining: null };
    }
    if (z >= Z_95) return { key, state: 'significant_positive', daysRemaining: null };
    if (z <= -Z_95) return { key, state: 'significant_negative', daysRemaining: null };

    const magnitude = Math.abs(z);
    if (magnitude <= 0) return { key, state: 'insufficient', daysRemaining: null };
    const daysRemaining = projectDaysRemaining(daysElapsed, (Z_95 / magnitude) ** 2);
    return daysRemaining == null
        ? { key, state: 'insufficient', daysRemaining: null }
        : { key, state: 'collecting', daysRemaining };
}

/**
 * Compute one readiness clock per metric, preserving the order of `metrics`.
 * Binary metrics read significance off the diff CI; continuous metrics off the
 * Mann-Whitney z. The caller decides which metrics and in what order (it maps
 * over the shared metric descriptors), so new metrics need no change here.
 */
export function computeMetricClocks(input: MetricClockInput): MetricClock[] {
    const nowMs = input.nowMs ?? Date.now();
    const daysElapsed = elapsedDays(input.startDate, nowMs);
    return input.metrics.map((m) =>
        m.kind === 'continuous'
            ? durationClock(m.key, { z: m.z ?? null }, daysElapsed)
            : binaryClock(
                  m.key,
                  { diff: m.diff ?? null, ciLow: m.ciLow ?? null, ciHigh: m.ciHigh ?? null },
                  daysElapsed
              )
    );
}
